import {
  BankAccountApi,
  BankAccountCreate,
  BankAccountDetailsUpdateRequest,
  BankAccountSummary,
  StoresApi,
  StripeAccountLinkRequest,
  StripeCustomConnectApi,
} from '@flipdish/api-client-typescript';
import axios from 'axios';

import { getApiEndPoint } from '../helpers/apibase';
import { CredentialsAuth } from '../helpers/utilities';
import { logger } from './loggerService';
import { mapServerError } from './utils/serverErrorMapper';

const api = new BankAccountApi(getApiEndPoint());
api.setDefaultAuthentication(new CredentialsAuth());

const stripeApi = new StripeCustomConnectApi(getApiEndPoint());
stripeApi.setDefaultAuthentication(new CredentialsAuth());

const baseURL = getApiEndPoint();

const portalApi = axios.create({
  baseURL: `${baseURL}/api/v1.0`,
  withCredentials: true,
});

// #region load
export async function getBankAccounts(appId: string) {
  try {
    const incomingMessage = await api.getBankAccounts(appId);
    return incomingMessage.body.Data;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region loadDetails

export async function getBankAccountById(appId: string, accountId: number) {
  try {
    const incomingMessage = await api.getBankAccountById(accountId, appId);
    return incomingMessage.body.Data;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region createBankAccount
export async function createBankAccount(appId: string, account: BankAccountCreate) {
  try {
    const incomingMessage = await api.createBankAccount(appId, account);
    return incomingMessage.body.Data;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region getFieldDefinitions
export async function getFieldDefinitions(appId: string) {
  try {
    const incomingMessage = await api.getFieldDefinitions(appId);
    return incomingMessage.body.Data.DefinitionsPerCountry;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region getFieldDefinitions
export async function getCountriesWithFieldDefinitions(appId: string) {
  try {
    const incomingMessage = await api.getCountriesWithFieldDefinitions(appId);
    return incomingMessage.body.Data;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region  updateBankAccount
export async function updateBankAccount(
  appId: string,
  id: number,
  account: BankAccountDetailsUpdateRequest
) {
  try {
    const incomingMessage = await stripeApi.updateBankAccountDetails(appId, id, account);
    return incomingMessage.body;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region createBankAccountAndConnectedAccount
export async function createBankAccountAndConnectedAccount(
  appId: string,
  account: BankAccountCreate
) {
  try {
    const incomingMessage = await stripeApi.createBankAccountAndConnectedAccount(appId, account);
    return incomingMessage.body.Data;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region setBankAccountBusinessType
export async function setBankAccountBusinessType(
  appId: string,
  account: {
    BankAccountId: number;
    BusinessType: BankAccountSummary.BusinessTypeEnum;
  }
) {
  try {
    const incomingMessage = await stripeApi.setBankAccountBusinessType(
      appId,
      account.BankAccountId!,
      account.BusinessType!.toString()
    );
    return incomingMessage.body.Data;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region createStripeConnectedAccountLink
export async function createStripeConnectedAccountLink(
  appId: string,
  stripeAccountLinkRequest: StripeAccountLinkRequest
) {
  try {
    const incomingMessage = await stripeApi.createStripeConnectedAccountLink(
      appId,
      stripeAccountLinkRequest.StripeId!,
      stripeAccountLinkRequest
    );
    // // to be removed after debugging https://flipdish.atlassian.net/browse/CLM-997
    logger.debug(`CreateStripeConnectedAccountLinkSuccess`, {
      stack: incomingMessage,
    });
    //@ts-ignore
    return incomingMessage.response.body.Data;
  } catch (incomingMessage) {
    // to be removed after debugging https://flipdish.atlassian.net/browse/CLM-997
    logger.error(`CreateStripeConnectedAccountLinkFailure`, {
      stack: incomingMessage,
    });

    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region deleteBankAccount
export async function removeBankAccount(appId: string, accountId: number) {
  try {
    await api.deleteBankAccount(appId, accountId);
    return;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region attachBankAccountToStore
export async function attachBankAccountToStore(appId: string, accountId: number, storeId: number) {
  try {
    await api.attachBankAccountToStore(appId, accountId, storeId);
    return;
  } catch (error) {
    throw mapServerError(error);
  }
}
// #endregion

// #region updateBankAccountState
export async function updateBankAccountState(
  appId: string,
  accountId: number,
  state: string,
  reason: string
) {
  try {
    await api.updateBankAccountState(appId, accountId, state, reason);
    return;
  } catch (error) {
    throw mapServerError(error);
  }
}
// #endregion

// #region get stores
const storesApi = new StoresApi(getApiEndPoint());
storesApi.setDefaultAuthentication(new CredentialsAuth());

export type GetStoresByAppIdProps = {
  appId: string;
  query?: string;
  page?: number;
  limit?: number;
};

export async function getStoresByAppId(props: GetStoresByAppIdProps) {
  try {
    const { appId, query, page, limit } = props;
    const incomingMessage = await storesApi.getStoresByAppId(appId, query, page, limit);
    return incomingMessage.body;
  } catch (incomingMessage) {
    throw mapServerError(incomingMessage);
  }
}
// #endregion

// #region updatePayoutSchedule
export async function updatePayoutSchedule(
  appId: string,
  stripeConnectedAccountId: string,
  interval: string
) {
  try {
    await stripeApi.updatePayoutSchedule(appId, stripeConnectedAccountId, interval);
  } catch (error) {
    throw mapServerError(error);
  }
}
// #endregion

export const bankingServices = {
  attachBankAccountToStore,
  createBankAccount,
  createBankAccountAndConnectedAccount,
  createStripeConnectedAccountLink,
  getBankAccountById,
  getBankAccounts,
  getCountriesWithFieldDefinitions,
  getFieldDefinitions,
  getStoresByAppId,
  removeBankAccount,
  setBankAccountBusinessType,
  updateBankAccount,
  updateBankAccountState,
  updatePayoutSchedule,
};
